import { useState } from 'react';
import { ChevronRight, ChevronDown, Folder, Star, Box } from 'lucide-react';
import type { CollectionTree } from '@maker-toolchest/shared';
import * as LucideIcons from 'lucide-react';

interface CollectionTreeNodeProps {
  collection: CollectionTree;
  level?: number;
  selectedCollectionIds?: string[];
  onSelect?: (collectionId: string) => void;
  showCheckbox?: boolean;
  ancestorSelected?: boolean; // True if any ancestor is directly selected
  ancestorColors?: string[]; // Array of ancestor colors for hierarchy lines
}

// Map of common icon names to Lucide components
const iconMap: Record<string, React.ComponentType<any>> = {
  folder: Folder,
  star: Star,
  box: Box,
  // Add more as needed - can be expanded
};

// Get icon component from name
function getIconComponent(iconName?: string | null): React.ComponentType<any> {
  if (!iconName) return Folder; // Default icon

  // Try exact match first
  if (iconMap[iconName]) {
    return iconMap[iconName];
  }

  // Try to find in Lucide exports (convert kebab-case to PascalCase)
  const pascalCase = iconName
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');

  const LucideIcon = (LucideIcons as any)[pascalCase];
  if (LucideIcon) {
    return LucideIcon;
  }

  // Fallback to default
  return Folder;
}

export function CollectionTreeNode({
  collection,
  level = 0,
  selectedCollectionIds = [],
  onSelect,
  showCheckbox = false,
  ancestorSelected = false,
  ancestorColors = [],
}: CollectionTreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(level < 2); // Auto-expand first 2 levels
  const hasChildren = collection.children && collection.children.length > 0;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleSelect = () => {
    if (onSelect) {
      onSelect(collection.id);
    }
  };

  const IconComponent = getIconComponent(collection.icon);

  // Determine selection state
  const isDirectlySelected = selectedCollectionIds.includes(collection.id);
  const isDescendantOfSelected = ancestorSelected && !isDirectlySelected;

  // Determine styling based on selection state
  let bgClass = '';
  let textClass = 'text-foreground';

  if (isDirectlySelected) {
    // Directly selected - bright primary color
    bgClass = 'bg-primary/20';
    textClass = 'text-primary font-semibold';
  } else if (isDescendantOfSelected) {
    // Descendant of selected - dimmer color
    bgClass = 'bg-primary/5';
    textClass = 'text-primary/60';
  }

  // Calculate reduced indentation (0.75rem per level instead of 1.5rem)
  const indentAmount = level * 0.75;

  return (
    <div>
      {/* Node */}
      <div
        className={`
          relative flex items-center gap-2 py-2 px-3 rounded-md cursor-pointer
          hover:bg-background-subtle transition-colors
          ${bgClass} ${textClass}
        `}
        style={{ paddingLeft: `${indentAmount}rem` }}
        onClick={handleSelect}
      >
        {/* Hierarchy Lines - render one for each ancestor */}
        {ancestorColors.map((color, index) => (
          <div
            key={index}
            className="absolute w-0.5"
            style={{
              top: 0,
              bottom: '-0.25rem', // Extend beyond bottom to fill the space-y-1 gap
              left: `${index * 0.75 + 0.375}rem`, // Position each line at its level
              backgroundColor: color || '#6b7280', // Use ancestor color or default gray
            }}
          />
        ))}

        {/* Expand/Collapse Button */}
        <button
          onClick={handleToggle}
          className="p-0.5 hover:bg-background-subtle rounded transition-colors"
          style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-foreground-muted" />
          ) : (
            <ChevronRight className="h-4 w-4 text-foreground-muted" />
          )}
        </button>

        {/* Checkbox (optional) */}
        {showCheckbox && (
          <input
            type="checkbox"
            checked={isDirectlySelected}
            onChange={handleSelect}
            onClick={(e) => e.stopPropagation()}
            className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
          />
        )}

        {/* Icon */}
        <IconComponent
          className="h-4 w-4 flex-shrink-0"
          style={{ color: collection.color || undefined }}
        />

        {/* Name */}
        <span className="flex-1 text-sm truncate">{collection.name}</span>

        {/* Model Count */}
        {collection.modelCount > 0 && (
          <span className="text-xs text-foreground-muted tabular-nums">
            {collection.modelCount}
          </span>
        )}
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="space-y-1 mt-1">
          {collection.children.map((child) => (
            <CollectionTreeNode
              key={child.id}
              collection={child}
              level={level + 1}
              selectedCollectionIds={selectedCollectionIds}
              onSelect={onSelect}
              showCheckbox={showCheckbox}
              ancestorSelected={ancestorSelected || isDirectlySelected}
              ancestorColors={[...ancestorColors, collection.color || '#6b7280']}
            />
          ))}
        </div>
      )}
    </div>
  );
}
